import { useMemo } from 'react';

import type { Color } from '@avodah-inc/types';
import { cn } from '@avodah-inc/ui/utils';

export type BucketTimerProps = {
  time: string;
  min: string;
  max: string;
  color: Color;
  section: number;
  completed: number;
  isEmbedded?: boolean;
};

const PADDING_SAUSAGES = 0.08;
const STROKE_WIDTH = 9;
const RADIUS = 65;
const WIDTH = 160;
const HEIGHT = 100;

export function BucketTimer(props: BucketTimerProps) {
  // How many radians each section
  const sectionRadians = Math.PI / props.section;
  const center = [WIDTH / 2, RADIUS];
  const sections = useMemo(
    () =>
      // 2 PI because y is inverted
      new Array(props.section).fill(2 * Math.PI).map((sec, ind) => {
        // 2 PI - the off set of the position of the section
        const startRad = sec - ind * sectionRadians - PADDING_SAUSAGES;
        const endRad = sec - (ind + 1) * sectionRadians + PADDING_SAUSAGES;
        const firstElem = center[0] ?? 0;
        const secondElem = center[1] ?? 0;
        const start = [
          firstElem + RADIUS * Math.cos(startRad),
          secondElem + RADIUS * Math.sin(startRad) + STROKE_WIDTH,
        ].map((n) => n.toFixed(0));
        const end = [
          firstElem + RADIUS * Math.cos(endRad),
          secondElem + RADIUS * Math.sin(endRad) + STROKE_WIDTH,
        ].map((n) => n.toFixed(0));
        return {
          start,
          end,
        };
      }),
    [props.section],
  );
  return (
    <svg height="100" width="160" xmlns="http://www.w3.org/2000/svg">
      <text
        x="0"
        y="100"
        className={cn('fill-onLight-medium', {
          'fill-primary-200': props.isEmbedded,
        })}
      >
        {props.min}
      </text>
      <text
        x={WIDTH}
        y={HEIGHT}
        className={cn('fill-onLight-medium', {
          'fill-primary-200': props.isEmbedded,
        })}
        style={{ transform: 'translate(-100%,0px)', transformBox: 'fill-box' }}
      >
        {props.max}
      </text>
      <text
        x={WIDTH / 2}
        y={RADIUS}
        style={{
          transform: 'translate(-50%,0px)',
          transformBox: 'fill-box',
          fontSize: '20px',
          fontWeight: 600,
        }}
      >
        {props.time}
      </text>
      {sections.map(({ start, end }, ind) => (
        <path
          key={ind}
          d={`M ${start[0]} ${start[1]} A ${RADIUS} ${RADIUS} 0 0 0 ${end[0]} ${end[1]}`}
          className={`fill-transparent ${
            sections.length - ind > props.completed
              ? 'stroke-gray-200'
              : `stroke-additional-${props.color}`
          }`}
          strokeLinecap="round"
          style={{ strokeWidth: `${STROKE_WIDTH}px` }}
        />
      ))}
    </svg>
  );
}
